import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flower2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function ConnectionBloomDisplay() {
  const { user } = useAuth();
  const [bloomCount, setBloomCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [prevCount, setPrevCount] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;

    const fetchBloomCount = async () => {
      const { data } = await (supabase as any)
        .from('profiles')
        .select('connection_bloom_count')
        .eq('user_id', user.id)
        .single();

      if (data?.connection_bloom_count != null) {
        const newCount = data.connection_bloom_count;
        
        // Check if bloom count increased since last visit
        const lastKnown = parseInt(localStorage.getItem('sin_bloom_count') || '0', 10);
        if (newCount > lastKnown && lastKnown > 0) {
          setShowNotification(true);
        }
        
        setBloomCount(newCount);
        localStorage.setItem('sin_bloom_count', String(newCount));
      }
    };

    fetchBloomCount();
  }, [user]);

  if (bloomCount === 0) return null;

  return (
    <>
      {/* Notification banner */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-4"
          >
            <Card className="border-primary/20 bg-primary/[0.04] overflow-hidden">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Flower2 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    Your message created a reply. Your tree just grew. 🌱
                  </p>
                </div>
                <button
                  onClick={() => setShowNotification(false)}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Persistent bloom counter */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Flower2 className="h-4 w-4 text-primary" />
        <span>
          Connection Blooms: <span className="font-semibold text-foreground">{bloomCount}</span>
        </span>
      </div>
    </>
  );
}
